// Единицы веса, валюта и импортные расходы заказов материалов — клиентская половина
// server/utils/order-units.js (план: server/docs/material-orders-import-roadmap.md).
// Канон строки (lbqty | qty, price_per_pcs) — lb и USD, его читает COGS. Что ввёл человек
// (qty_entered, price_entered) — в единице и валюте поставщика; у старых строк это NULL и
// восстанавливается из канона. Настройки поставщика в заказе (currency, fx_rate, weight_unit,
// is_import, customs_compliance) приходят из /order-extra-costs вместе с доп. расходами.

export const LB_PER_KG = 2.20462;
export const DEFAULT_SETTINGS = { currency: 'USD', fx_rate: null, weight_unit: 'lb', is_import: 0, customs_compliance: 0 };

const num = v => (v == null || v === '' ? 0 : Number(v)) || 0;

// Множители: kUnit — lb за единицу ввода (kg → 2.20462), kFx — USD за единицу валюты
export function factors(settings = {}, weight = false) {
  const s = { ...DEFAULT_SETTINGS, ...settings };
  return {
    kUnit: weight && s.weight_unit === 'kg' ? LB_PER_KG : 1,
    kFx:   s.currency === 'EUR' && Number(s.fx_rate) > 0 ? Number(s.fx_rate) : 1,
  };
}

// Введённое количество (в единице поставщика); у штучных вкладок — qty как есть
export function enteredQty(row, settings, weight) {
  if (weight && row.qty_entered != null && row.qty_entered !== '') return num(row.qty_entered);
  const canon = num(weight ? row.lbqty : row.qty);
  return weight ? canon / factors(settings, weight).kUnit : canon;
}

// Введённая цена (за единицу поставщика, в его валюте)
export function enteredPrice(row, settings, weight) {
  if (row.price_entered != null && row.price_entered !== '') return num(row.price_entered);
  const { kUnit, kFx } = factors(settings, weight);
  return num(row.price_per_pcs) * kUnit / kFx;
}

// Количество в lb (весовые вкладки) или pcs — то, чем делятся доп. расходы
export function canonQty(row, settings, weight) {
  return enteredQty(row, settings, weight) * factors(settings, weight).kUnit;
}

// Сумма строки в валюте поставщика и в USD
export function rowAmounts(row, settings, weight) {
  const native = enteredQty(row, settings, weight) * enteredPrice(row, settings, weight);
  return { native, usd: native * factors(settings, weight).kFx };
}

// Доп. расходы на единицу (lb | pcs) по позициям заказа — то, что сервер запишет в other_expenses
// (зеркало otherExpensesPerUnit в server/utils/order-units.js, числа в тестах те же):
//   общие расходы поставщика (+ Customs & Compliance у импортного) ÷ всё его количество — каждой его позиции;
//   Duties + Lab Testing строк позиции ÷ количество этой позиции — только ей.
// rows: [{ supplierID, itemID, qty (lb | pcs), duties, lab_testing }]; costs: { [supplierID]: строка order_extra_costs }.
// Возвращает Map itemID → USD за единицу; позиции поставщика без строки расходов в Map не попадают.
export function extraPerUnitByItem(rows, costs = {}) {
  const out = new Map();
  const add = (item, v) => out.set(item, (out.get(item) || 0) + v);
  const qtyOf = list => list.reduce((s, r) => s + num(r.qty), 0);
  for (const [sid, c] of Object.entries(costs)) {
    if (!c || !(Number(sid) > 0)) continue;
    const supRows = rows.filter(r => Number(r.supplierID) === Number(sid));
    const items = [...new Set(supRows.map(r => r.itemID))];
    items.forEach(item => add(item, 0));
    const imp    = c.is_import ? 1 : 0;
    const total  = num(c.packaging) + num(c.freight_handling) + num(c.miscellaneous) + imp * num(c.customs_compliance);
    const supQty = qtyOf(supRows);
    if (total > 0 && supQty > 0) items.forEach(item => add(item, total / supQty));
    if (!imp) continue;
    for (const item of items) {
      const perItem = supRows.filter(r => r.itemID === item).reduce((s, r) => s + num(r.duties) + num(r.lab_testing), 0);
      const itemQty = qtyOf(rows.filter(r => r.itemID === item));
      if (perItem > 0 && itemQty > 0) add(item, perItem / itemQty);
    }
  }
  return out;
}

export const currencySymbol = currency => (currency === 'EUR' ? '€' : '$');
export const unitLabel = (settings, weight) => (weight ? (settings?.weight_unit === 'kg' ? 'kg' : 'lb') : 'pcs');

// Есть ли введённые импортные суммы — подтверждение при снятии галочки Import order
export const hasImportCosts = (settings, rows) =>
  num(settings?.customs_compliance) > 0 || rows.some(r => num(r.duties) > 0 || num(r.lab_testing) > 0);

// ── Загрузка заказа из файла (CSV / XLSX) ────────────────────────────────────
// Валюта, курс, единица и Import order выбираются в окне загрузки — одни на весь файл.
// Количество и цена в файле — как в инвойсе, в выбранных единице и валюте; сервер сам
// пересчитывает их в lb и USD. Duties и Lab Testing — необязательные колонки, всегда USD.
export const UPLOAD_SETTINGS = { currency: 'USD', fx_rate: '', weight_unit: 'lb', is_import: false };

// Числа одной строки файла. Колонки: qty (у семян принимается и старое имя lbqty),
// price (или старое price_per_pcs), duties, lab_testing (только семена).
// Возвращает { qty, price, duties, lab_testing, errors } — errors без номера строки.
export function uploadNumbers(row, weight) {
  const cell = (...keys) => {
    for (const k of keys) {
      const v = row[k];
      if (v !== undefined && v !== null && String(v).trim() !== '') return String(v).trim();
    }
    return '';
  };
  const errors = [];
  const read = (label, raw, { required = false, integer = false } = {}) => {
    if (raw === '') {
      if (required) errors.push(`Missing required field "${label}"`);
      return required ? null : 0;
    }
    const n = Number(raw);
    if (!Number.isFinite(n) || n < 0 || (integer && !Number.isInteger(n))) {
      errors.push(`"${label}" must be a ${integer ? 'whole number' : 'number'}, 0 or more`);
      return null;
    }
    return n;
  };
  return {
    qty:         read('qty', cell('qty', ...(weight ? ['lbqty'] : [])), { required: true, integer: !weight }),
    price:       read('price', cell('price', 'price_per_pcs'), { required: true }),
    duties:      read('duties', cell('duties')),
    lab_testing: weight ? read('lab_testing', cell('lab_testing')) : 0,
    errors,
  };
}

// Итоги предпросмотра файла: количество в единице файла и в lb, сумма в валюте файла и в USD,
// построчные импортные расходы (USD)
export function uploadTotals(rows, settings, weight) {
  const { kUnit, kFx } = factors(settings, weight);
  const qty    = rows.reduce((s, r) => s + num(r.qty), 0);
  const native = rows.reduce((s, r) => s + num(r.qty) * num(r.price), 0);
  return {
    qty, qtyLb: qty * kUnit, native, usd: native * kFx,
    importUsd: rows.reduce((s, r) => s + num(r.duties) + num(r.lab_testing), 0),
  };
}

// Настройки окна загрузки в виде, который принимает сервер; error — почему сохранять нельзя
export function uploadSettingsPayload(settings, weight) {
  const isEUR = settings.currency === 'EUR';
  const rate = parseFloat(settings.fx_rate);
  if (isEUR && !(rate > 0)) return { error: 'Enter the EUR→USD rate' };
  return {
    settings: {
      currency:    isEUR ? 'EUR' : 'USD',
      fx_rate:     isEUR ? rate : null,
      weight_unit: weight && settings.weight_unit === 'kg' ? 'kg' : 'lb',
      is_import:   settings.is_import ? 1 : 0,
    },
  };
}

// Число для поля ввода без хвостовых нулей: '0.00' → '0', 10.0018 → '10.002'
export const trimNum = (v, digits) => (v == null || v === '' ? '' : String(+Number(v).toFixed(digits)));

export const fmt = (v, digits = 2) =>
  Number(v || 0).toLocaleString('en-US', { minimumFractionDigits: digits, maximumFractionDigits: digits });
